/**
 * v2 hero: the live energy flow inside the hero panel. Hub-and-spoke
 * around the inverter (mirrors the Victron wiring), bezier edges,
 * energy as dots travelling the edge via SMIL animateMotion (no JS
 * per frame; speed from wattage, direction from sign), glowing node
 * discs with the day's kWh, a SOC arc on the battery, a header strip.
 * Uses the hero-* tokens so it follows the theme.
 * docs/customer-app.md > Front-end > Energy flow hero
 */
import type { Live, Today } from "../api";
import { batteryStateLabel, formatPower } from "../format";

export type FlowNodeKind = "pv" | "grid" | "load" | "battery" | "ev";

interface Props {
  live: Live;
  today?: Today | null;
  /** Makes the discs tappable - Home opens a drill-in modal with the
   *  node's power graph. Absent = plain visual (login preview etc.). */
  onNodeClick?: (kind: FlowNodeKind) => void;
}

const VB_W = 400;
const VB_H = 470;
const CX = VB_W / 2;
const CY = 215;
const NODE_R = 33;
const OFFSET = 132;

// Perfect diamond - all four nodes equidistant from the hub.
const SUN     = { x: CX, y: CY - OFFSET };
const GRID    = { x: CX - OFFSET, y: CY };
const HOME    = { x: CX + OFFSET, y: CY };
const BATTERY = { x: CX, y: CY + OFFSET };
// EV hangs off the Huis node (it's a house load, wherever the wallbox
// is wired) - only rendered when the site has evcc configured. It sits
// ABOVE Huis with its text on the far side, so the connecting edge
// never crosses Huis's kWh labels (which live below the disc).
const EV      = { x: HOME.x + 4, y: HOME.y - 138 };
const EV_R    = 24;

const IDLE_W = 100;

function shortKwh(kwh: number): string {
  if (Math.abs(kwh) >= 10) return `${Math.round(kwh)} kWh`;
  return `${kwh.toFixed(1)} kWh`;
}

/** One totals line under a node: the measured kWh, and in light grey
 *  where today is expected to end ("12 kWh / 35 kWh"). The expected
 *  part is dropped when unknown. */
interface TotalLine {
  prefix?: string;        // "→ " / "← " (net), "↓ " / "↑ " (batterij)
  measured: number;
  expected?: number | null;
}

function totalLine(measured: number, expected: number | null | undefined, prefix?: string): TotalLine {
  return { prefix, measured, expected };
}

// Node colors follow the theme tokens - the hero panel is light in
// light mode and dark in dark mode, and the tokens are tuned per theme.
const C = {
  sun: "var(--color-sun)",
  grid: "var(--color-grid)",
  home: "var(--color-home)",
  battery: "var(--color-accent)",
  ev: "var(--color-ev)",
};

export function EnergyFlow({ live, today, onNodeClick }: Props) {
  const { pv_w, load_w, battery_w, grid_w, soc } = live;
  const state = batteryStateLabel(live);

  return (
    <div className="hero-panel p-4 sm:p-5">
      {/* header strip */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: C.battery }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: C.battery }} />
          </span>
          <span className="text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--hero-ink-mute)" }}>
            Live
          </span>
          {state.label !== "Inactief" && (
            <span
              className="truncate rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{ background: "var(--hero-chip)", color: "var(--hero-ink)" }}
            >
              {state.label}
              {state.hint ? ` · ${state.hint}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1 shrink-0">
          <span className="display-num text-2xl" style={{ color: C.battery }}>
            {Math.round(soc)}
          </span>
          <span className="text-sm font-semibold" style={{ color: "var(--hero-ink-mute)" }}>%</span>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto max-w-md mx-auto"
        role="img"
        aria-label="Live energiestroom"
      >
        <defs>
          <filter id="dotGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="2.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Direction convention matches v1:  watts > 0 → dots travel a → b.
              Sun → hub:  pv_w ≥ 0 (produce → in)
              Grid → hub: grid_w > 0 import, < 0 export (reverses)
              hub → Home: load_w ≥ 0 (consume → out)
              hub → Battery: battery_w > 0 charging, < 0 discharging */}
        <FlowEdge a={SUN} b={{ x: CX, y: CY }} watts={pv_w} color={C.sun} bend={0} />
        <FlowEdge a={GRID} b={{ x: CX, y: CY }} watts={grid_w} color={C.grid} bend={0} />
        <FlowEdge a={{ x: CX, y: CY }} b={HOME} watts={load_w} color={C.home} bend={0} />
        <FlowEdge a={{ x: CX, y: CY }} b={BATTERY} watts={battery_w} color={C.battery} bend={0} />
        {/* Auto altijd zichtbaar zodra de site evcc heeft (Stefan,
            10-09: eerst bewust verborgen zonder auto, nu altijd) -
            los van de laadpaal dooft de tak gewoon uit. */}
        {live.ev && (
          <FlowEdge a={HOME} b={EV} watts={live.ev.connected ? live.ev.charge_power_w : 0} color={C.ev} bend={0} />
        )}

        {/* inverter anchor */}
        <circle cx={CX} cy={CY} r={7} fill="var(--hero-chip)" stroke="var(--hero-line)" strokeWidth={1.2} />
        <circle cx={CX} cy={CY} r={2.6} fill="var(--hero-ink-mute)" />

        <FlowNode pos={SUN} color={C.sun} label="Zon" onClick={onNodeClick ? () => onNodeClick("pv") : undefined} icon={<SunGlyph />} active={pv_w >= IDLE_W}
          textPos="above"
          lines={today ? [totalLine(today.pv_kwh, today.expected?.pv_kwh)] : []} />
        <FlowNode pos={GRID} color={C.grid} label="Net" onClick={onNodeClick ? () => onNodeClick("grid") : undefined} icon={<GridGlyph />} active={Math.abs(grid_w) >= IDLE_W}
          lines={today ? [
            // Net sits left of the hub: import flows right, export left
            // (Stefan, 2026-10-20). Battery below the hub keeps down/up.
            totalLine(today.grid_in_kwh, today.expected?.grid_in_kwh, "→ "),
            totalLine(today.grid_out_kwh, today.expected?.grid_out_kwh, "← "),
          ] : []} />
        <FlowNode pos={HOME} color={C.home} label="Huis" onClick={onNodeClick ? () => onNodeClick("load") : undefined} icon={<HomeGlyph />} active={load_w >= IDLE_W}
          lines={today ? [totalLine(today.load_kwh, today.expected?.load_kwh)] : []} />
        <FlowNode pos={BATTERY} color={C.battery} label="Batterij" onClick={onNodeClick ? () => onNodeClick("battery") : undefined} icon={<BatteryGlyph />} active={Math.abs(battery_w) >= IDLE_W}
          soc={soc}
          lines={today ? [
            totalLine(today.to_battery_kwh, today.expected?.to_battery_kwh, "↓ "),
            totalLine(today.from_battery_kwh, today.expected?.from_battery_kwh, "↑ "),
          ] : []} />
        {/* Auto alleen in beeld als hij hangt: een losgekoppelde
            auto levert geen actuele cijfers meer (evcc bevriest ze). */}
        {live.ev && (
          <FlowNode pos={EV} color={C.ev} label="Auto" onClick={onNodeClick ? () => onNodeClick("ev") : undefined} icon={<CarGlyph />}
            active={live.ev.connected && live.ev.charge_power_w >= IDLE_W} r={EV_R} textPos="above"
            lines={
              live.ev.connected && live.ev.vehicle_soc_pct != null
                ? [`${Math.round(live.ev.vehicle_soc_pct)}%`]
                : !live.ev.connected ? ["los"] : []
            } />
        )}
      </svg>
    </div>
  );
}

/* ---------------- edges: curved rail + traveling dots ---------------- */

interface EdgeProps {
  a: { x: number; y: number };
  b: { x: number; y: number };
  watts: number;
  color: string;
  /** Perpendicular bow of the curve (0 = straight). */
  bend: number;
}

function FlowEdge({ a, b, watts, color, bend }: EdgeProps) {
  const mag = Math.abs(watts);
  const idle = mag < IDLE_W;

  // Pull endpoints in so the rail meets the node's rim, not its centre.
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const insetA = Math.hypot(a.x - CX, a.y - CY) < 1 ? 10 : NODE_R + 3;
  const insetB = Math.hypot(b.x - CX, b.y - CY) < 1 ? 10 : NODE_R + 3;
  const a2 = { x: a.x + (dx / len) * insetA, y: a.y + (dy / len) * insetA };
  const b2 = { x: b.x - (dx / len) * insetB, y: b.y - (dy / len) * insetB };

  // Quadratic bezier control point, offset perpendicular by `bend`.
  const mx = (a2.x + b2.x) / 2 - (dy / len) * bend;
  const my = (a2.y + b2.y) / 2 + (dx / len) * bend;
  const d = `M ${a2.x} ${a2.y} Q ${mx} ${my} ${b2.x} ${b2.y}`;

  // Dot travel time: 5 kW ≈ 1 s, clamped to a sane range.
  const dur = idle ? 0 : Math.max(0.7, Math.min(3.2, 5000 / mag));
  const reverse = watts < 0;

  // Label pill sits ON the rail at its midpoint - the solid pill
  // masks the line + passing dots, so all four labels align with the
  // cross axes instead of floating beside them.
  const lt = 0.5;
  const lx = (1 - lt) * (1 - lt) * a2.x + 2 * (1 - lt) * lt * mx + lt * lt * b2.x;
  const ly = (1 - lt) * (1 - lt) * a2.y + 2 * (1 - lt) * lt * my + lt * lt * b2.y;

  const DOTS = mag >= 2500 ? 3 : 2;

  return (
    <g>
      <path d={d} fill="none" stroke="var(--hero-line)" strokeWidth={2.5} strokeLinecap="round" />
      {!idle && (
        <>
          <path d={d} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" opacity={0.28} />
          {Array.from({ length: DOTS }).map((_, i) => (
            <circle key={i} r={3.2} fill={color} filter="url(#dotGlow)">
              <animateMotion
                dur={`${dur}s`}
                begin={`${(dur / DOTS) * i - dur}s`}
                repeatCount="indefinite"
                path={d}
                calcMode="linear"
                keyPoints={reverse ? "1;0" : "0;1"}
                keyTimes="0;1"
              />
            </circle>
          ))}
        </>
      )}
      {/* Power label is ALWAYS visible - colored + bold when flowing,
          muted when idle. "0 W" carries real information (zon 's
          nachts, batterij in rust). */}
      <g transform={`translate(${lx}, ${ly})`}>
        <rect x={-28} y={-11} width={56} height={22} rx={11}
          fill="var(--hero-from)" stroke="var(--hero-line)" strokeWidth={1} />
        <text textAnchor="middle" dominantBaseline="central" fontSize={12}
          fontWeight={idle ? 600 : 700}
          fill={idle ? "var(--hero-ink-mute)" : color} className="tabular">
          {formatPower(mag)}
        </text>
      </g>
    </g>
  );
}

/* ---------------- nodes ---------------- */

interface NodeProps {
  pos: { x: number; y: number };
  color: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  /** Plain strings (EV) or measured/expected kWh pairs. */
  lines: (string | TotalLine)[];
  onClick?: () => void;
  /** Battery only: draws a live SOC arc around the disc. */
  soc?: number;
  /** "above" puts label+totals on the hub-far side of a TOP node so
   * they never collide with the rail below it (Zon). */
  textPos?: "above" | "below";
  /** Disc radius override - the EV satellite is drawn smaller. */
  r?: number;
}

function FlowNode({ pos, color, label, icon, active, lines, soc, textPos = "below", r = NODE_R, onClick }: NodeProps) {
  const ARC_R = r + 6;
  const circumference = 2 * Math.PI * ARC_R;
  const rimR = soc != null ? ARC_R : r;
  return (
    <g
      transform={`translate(${pos.x}, ${pos.y})`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      aria-label={onClick ? `${label} - details` : undefined}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      {/* invisible tap target well past the disc - thumbs are blunt */}
      {onClick && <circle r={r + 16} fill="transparent" />}
      {active && (
        <circle r={r + 11} fill={color} className="node-breathe" />
      )}
      {/* Disc: faint color tint so the colored icon always has contrast. */}
      <circle r={r} fill={color} opacity={0.14} />
      <circle r={r} fill="transparent" stroke={color} strokeWidth={1.6} />
      {soc != null && (
        <>
          <circle r={ARC_R} fill="none" stroke="var(--hero-line)" strokeWidth={3} />
          <circle
            r={ARC_R}
            fill="none"
            stroke={color}
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray={`${(circumference * Math.max(0, Math.min(100, soc))) / 100} ${circumference}`}
            transform="rotate(-90)"
          />
        </>
      )}
      <g transform="translate(-11, -11)" style={{ color }}>{icon}</g>
      {textPos === "below" ? (
        <>
          <text textAnchor="middle" y={rimR + 17} fontSize={11.5}
            fill="var(--hero-ink-mute)" fontWeight={600}>
            {label}
          </text>
          {lines.map((line, i) => (
            <text key={i} textAnchor="middle" y={rimR + 32 + i * 14}
              fontSize={12} fontWeight={700} fill="var(--hero-ink)" className="tabular">
              <LineContent line={line} />
            </text>
          ))}
        </>
      ) : (
        <>
          {/* stacked upward: totals on top, then the name just above the disc */}
          {lines.map((line, i) => (
            <text key={i} textAnchor="middle"
              y={-(rimR + 24) - (lines.length - 1 - i) * 14}
              fontSize={12} fontWeight={700} fill="var(--hero-ink)" className="tabular">
              <LineContent line={line} />
            </text>
          ))}
          <text textAnchor="middle" y={-(rimR + 10)} fontSize={11.5}
            fill="var(--hero-ink-mute)" fontWeight={600}>
            {label}
          </text>
        </>
      )}
    </g>
  );
}

/** "↓ 1.4 kWh" in ink, then " / 2.1 kWh" in the muted hero colour and a
 *  lighter weight: the expected end-of-day total next to the measured one
 *  (Stefan, 2026-10-05). */
function LineContent({ line }: { line: string | TotalLine }) {
  if (typeof line === "string") return <>{line}</>;
  return (
    <>
      {line.prefix ?? ""}{shortKwh(line.measured)}
      {line.expected != null && (
        <tspan fill="var(--hero-ink-mute)" fontWeight={500} fontSize={11}>
          {` / ${shortKwh(line.expected)}`}
        </tspan>
      )}
    </>
  );
}

/* ---------------- glyphs (inline, no icon-lib dependency) ---------------- */

const glyph = {
  width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
  stroke: "currentColor", strokeWidth: 2,
  strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
};

function SunGlyph() {
  return (
    <svg {...glyph}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function HomeGlyph() {
  return (
    <svg {...glyph}>
      <path d="M3 12l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}
function BatteryGlyph() {
  return (
    <svg {...glyph}>
      <rect x="3" y="7" width="16" height="10" rx="2" />
      <line x1="21" y1="10" x2="21" y2="14" />
      <path d="M11 9l-2 4h3l-2 4" />
    </svg>
  );
}
function GridGlyph() {
  return (
    <svg {...glyph}>
      <path d="M9 2l-2 8h4l-1 5 4-8h-3l1-5z" />
    </svg>
  );
}
function CarGlyph() {
  return (
    <svg {...glyph}>
      <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11" />
      <path d="M4 11h16a1 1 0 0 1 1 1v4h-2M3 16H2v-4a1 1 0 0 1 1-1" />
      <circle cx="7" cy="16" r="1.6" />
      <circle cx="17" cy="16" r="1.6" />
    </svg>
  );
}
